import Vue from 'vue'
import Vuex from 'vuex'
import { getUserRoutes } from '@/api'
import { constantRoutes } from '@/router'
import adminSession from '@/utils/adminSession'

// 后台路由由数据库返回，使用显式组件上下文保证新增页面会被 Webpack 收集。
const viewComponents = require.context('../views', true, /\.vue$/)

function loadView(componentPath) {
    const normalizedPath = componentPath.replace(/^\/|\/$/g, '')
    const componentKey = `./${normalizedPath}.vue`
    if (!viewComponents.keys().includes(componentKey)) {
        console.error(`组件加载失败: ${normalizedPath}`)
        return Promise.resolve({ render: h => h('div', `❌ 找不到组件: ${normalizedPath}`) })
    }
    const module = viewComponents(componentKey)
    return Promise.resolve(module.default || module)
}

Vue.use(Vuex)

// 递归转换组件
function filterAsyncRoutes(routes) {
    const res = []
    routes.forEach(route => {
        const tmp = { ...route }
        if (tmp.component) {
            if (tmp.component === 'Layout') {
                tmp.component = () => import('@/layout/index.vue')
            } else if (tmp.component === 'ParentView') {
                tmp.component = () => import('@/components/ParentView/index.vue')
            } else {
                // 清理路径，防止双斜杠
                const componentPath = tmp.component.replace(/^\/|\/$/g, '')
                tmp.component = () => loadView(componentPath)
            }
        }
        if (tmp.children && tmp.children.length) {
            tmp.children = filterAsyncRoutes(tmp.children)
        }
        res.push(tmp)
    })
    return res
}

// 标准化路由结构
function normalizeBackendRoutes(routes) {
    return routes.filter(r => r.type !== 2).map(route => {
        const meta = { title: route.title, icon: route.icon, type: route.type }
        if (route.type === 1 && route.parentId === 0) {
            return {
                path: route.path,
                component: 'Layout',
                meta: { ...meta, type: 0 },
                children: [
                    {
                        path: '',
                        component: route.component,
                        name: route.title,
                        meta: meta
                    }
                ]
            }
        }
        return {
            path: route.path,
            component: route.component,
            name: route.title,
            meta: meta,
            children: route.children ? normalizeBackendRoutes(route.children) : []
        }
    })
}

export default new Vuex.Store({
    state: {
        token: adminSession.getAdminToken(),
        routes: [],
        addRoutes: [],
        hasLoadedRoutes: false // 【关键】使用这个标志位
    },
    mutations: {
        SET_TOKEN: (state, token) => {
            state.token = token
            adminSession.setAdminToken(token)
        },
        SET_ROUTES: (state, routes) => {
            state.addRoutes = routes
            state.routes = constantRoutes.concat(routes)
            state.hasLoadedRoutes = true // 【关键】标记为已加载
        },
        RESET_ROUTES: (state) => {
            state.hasLoadedRoutes = false
            state.routes = []
        }
    },
    actions: {
        GenerateRoutes({ commit }) {
            return new Promise((resolve, reject) => {
                getUserRoutes().then(res => {
                    // 即使 res.data 为空，流程也能走通
                    const normalizedData = normalizeBackendRoutes(res.data || [])
                    const accessedRoutes = filterAsyncRoutes(normalizedData)
                    accessedRoutes.push({ path: '*', redirect: '/404', hidden: true })
                    commit('SET_ROUTES', accessedRoutes)
                    resolve(accessedRoutes)
                }).catch(err => {
                    const status = err.response && err.response.status
                    if (status === 401 || status === 403) {
                        reject(err)
                        return
                    }
                    console.error("路由接口请求失败:", err)
                    // 即使失败，也要让流程走下去，避免死循环
                    commit('SET_ROUTES', [])
                    resolve([])
                })
            })
        },
        Logout({ commit }) {
            commit('SET_TOKEN', '')
            commit('RESET_ROUTES')
            adminSession.clearAdminToken()
            location.reload()
        }
    }
})
